import { useEffect, useRef } from 'react';

export default function PhilosophyChapter03() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.fade-up, .fade-up-item, .scale-in').forEach((el) => el.classList.add('visible'));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="chapter3" className="py-20 px-6 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12 fade-up">
          <p className="text-lg font-bold mb-4" style={{ color: '#3E5F8F' }}>03</p>
          <h2 className="text-3xl md:text-4xl font-normal text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>
            マウスピース矯正<br />向き・不向きの正体
          </h2>
          <p className="text-base text-gray-700 leading-relaxed">
            マウスピース矯正ができるかどうか。<br />
            それは、あなたの「意志」や「自己管理」だけで<br />
            決まるものではありません。<br /><br />
            私たちが診査で最も重視するのは、<br />
            歯根の位置と骨の厚さという、<br />
            変えることのできない物理的な条件です。
          </p>
        </div>

        <div className="mb-12 bg-gray-50 rounded-lg p-8 fade-up">
          <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>なぜ「向かない」ケースがあるのか</h3>
          <p className="text-base text-gray-700 mb-8">
            歯は、目に見える「頭(歯冠)」と、骨に埋まっている<br />
            「根っこ(歯根)」でできています。
          </p>
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-lg p-6 transition-all duration-500 hover:-translate-y-1 fade-up-item">
              <p className="text-sm font-bold text-gray-900 mb-4">・得意なケース</p>
              <p className="text-sm text-gray-700 mb-4">根っこの位置はそのままで、頭を後ろに「倒して」並べれば済む場合。</p>
              <img
                alt="得意なケース"
                className="w-full rounded-lg"
                src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-971x1803_v-frms_webp_cb01e1a7-27a1-4630-a665-59eb19bf0a16_small.webp"
              />
            </div>
            <div className="bg-white rounded-lg p-6 transition-all duration-500 hover:-translate-y-1 fade-up-item">
              <p className="text-sm font-bold text-gray-900 mb-4">・慎重な判断が必要なケース</p>
              <p className="text-sm text-gray-700 mb-4">根っこそのものを大きく「横にスライド」させなければならない場合。</p>
              <img
                alt="慎重な判断が必要なケース"
                className="w-full rounded-lg"
                src="https://storage.googleapis.com/studio-design-asset-files/projects/Kwa5AGrYaX/s-971x1803_v-frms_webp_541a84ae-fdb3-4962-8b04-03991dc749f1_small.webp"
              />
            </div>
          </div>
          <p className="text-base text-gray-700">
            後者の場合、マウスピースだけで無理に動かそうとすると、<br />
            噛み合わせが不安定になったり、<br />
            数年後に後戻りしたりするリスクが高まります。
          </p>
        </div>

        <div className="mb-12 bg-gray-50 rounded-lg p-8 fade-up">
          <h3 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Noto Serif JP, serif' }}>私たちが「NO」と言う理由</h3>
          <p className="text-base text-gray-700 leading-relaxed">
            「すべてをマウスピースで解決できる」<br />
            と断言することは、<br />
            医療として誠実ではないと私たちは考えます。<br /><br />
            物理的な限界を無視して進める治療は、<br />
            必ずどこかで歪みが出ます。<br />
            だからこそ、私たちは診断結果をもとに、<br />
            「マウスピースがベストなのか、それともワイヤーの方が無理がないのか」を隠さずにお伝えします。
          </p>
        </div>

        <div className="rounded-lg p-8 fade-up" style={{ backgroundColor: '#EEF3F9' }}>
          <h4 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Noto Serif JP, serif' }}>まとめ</h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            向き不向きは、あなたの性格ではなく「骨と根の条件」で決まります。<br /><br />
            「頭」を倒す動きは得意ですが、「根」を動かす動きには物理的な限界があります。<br /><br />
            無理な計画を立てないこと。<br />
            それが、10年後も後悔しないための「正しい判断」への第一歩です。
          </p>
        </div>
      </div>
    </section>
  );
}